import { useAtsConversationApi, useFFRestApiWithOptions } from '@/composables/useApi';
import type { Employer, Job, UserProfile } from '@factoryfixinc/ats-interfaces';
import { ErrorService } from '@/core/shared/errors/error.service';
import { HttpStatus } from '@/core/shared/errors/enums/http-status.enum';
import { InternalError } from '@/core/shared/errors/internal.error';
import { parseApiError } from '@/core/shared/errors/parse-api-error.util';
import { ExistingApplicationConflictError } from './existing-application-conflict.error';
import { ActiveThreadExistsError } from './active-thread-exists.error';

const EXISTING_APPLICATION_CODE = 'invalid_status_for_update';
const ACTIVE_THREAD_CODE = 'active_thread_exists';

export enum EngagementMode {
  SCREEN = 'SCREEN',
  CONTACT = 'CONTACT',
  SKIP = 'SKIP',
}

export interface UploadResumeRequest {
  resumeDataUrl: string;
  engagementMode: EngagementMode;
  fileName: string;
}

export interface UploadResumeResponse {
  uploadId: number;
}

export default class OutreachPersistence {
  /**
   * Initiates manual AI outreach for a single candidate
   *
   * @param employerId - ID of the employer initiating outreach
   * @param proUserProfileId - ID of the candidate to reach out to
   * @param jobId - ID of the job for which to initiate outreach
   * @throws {InternalError} When validation fails or unexpected status returned
   */
  async initiateManualOutreach(
    employerId: Employer['id'],
    proUserProfileId: UserProfile['id'],
    jobId: Job['id'],
  ): Promise<void> {
    const url = `outreach/employer/${employerId}/pro/${proUserProfileId}/job/${jobId}`;

    // Read the raw response body via `.text()` and parse it ourselves on the
    // error path (mirrors `me.persistence.ts` `inviteEmployerMember`). The
    // `parseError: true` composable's stringify-merge spreads a raw-string
    // ctx.data character-by-character into the merged error, which silently
    // breaks structured `code` lookups.
    const {
      data: responseText,
      error,
      statusCode,
    } = await useAtsConversationApi(url).post().text();

    if (error.value) {
      // Without `parseError: true`, vueuse leaves `error.value` as an Error
      // object — `.includes()` blows up on it (production Sentry caught this
      // post-merge). Substring-match against the raw response body string
      // instead; structured matching reads `parsedBody.code`.
      const body = responseText.value ?? '';
      const parsedBody = parseApiError(responseText.value);

      // Existing application past the early-outreach phase — surfaced as 409
      // by ats-conversation. Send a typed error so the service layer can show
      // a candidate-specific toast instead of the generic "1 candidate failed"
      // copy.
      if (
        statusCode.value === HttpStatus.CONFLICT &&
        parsedBody.code === EXISTING_APPLICATION_CODE
      ) {
        throw new ExistingApplicationConflictError({
          data: { proUserProfileId, jobId },
        });
      }

      // The AI recruiter already has an active thread with this candidate; the request was blocked
      // without side effects, so it is not a failure and is not reported to Sentry.
      if (statusCode.value === HttpStatus.CONFLICT && parsedBody.code === ACTIVE_THREAD_CODE) {
        throw new ActiveThreadExistsError({
          data: { proUserProfileId, jobId },
        });
      }

      // Job not active or doesn't have activeOutreachEnabled
      if (body.includes('must have active outreach enabled')) {
        throw new InternalError('Job must be active for AI outreach', {
          data: { proUserProfileId, jobId, error: body },
        });
      }

      // Project doesn't have copilot enabled
      if (body.includes('must have copilot enabled')) {
        throw new InternalError('Job must have AI copilot enabled', {
          data: { proUserProfileId, jobId, error: body },
        });
      }

      // Job not found
      if (body.includes('not found')) {
        throw new InternalError('Job not found', {
          data: { proUserProfileId, jobId, error: body },
        });
      }

      // Unrecognized failure — capture to Sentry directly. InternalError is
      // suppressed from Sentry capture by design (introduced in PR #1471 to
      // silence known business-conflict alerts), so genuine backend faults
      // like NoAvailableConversationSourceError would otherwise vanish into
      // a "Failed to initiate outreach" snackbar with no monitoring signal.
      ErrorService.captureException(error.value);
      throw new InternalError('Failed to initiate AI outreach', {
        data: { proUserProfileId, jobId, error: body },
      });
    }

    // Success path: ats-conversation returns 204 No Content. Anything else is
    // unexpected.
    if (statusCode.value !== HttpStatus.NO_CONTENT) {
      throw new InternalError('AI outreach request returned unexpected status', {
        data: { proUserProfileId, jobId, statusCode: statusCode.value },
      });
    }
  }

  /**
   * Uploads a candidate resume for processing
   *
   * @param jobId - ID of the job to associate the resume with
   * @param request - Upload request containing base64 resume, engagement mode, and filename
   * @returns The upload ID for tracking
   * @throws {InternalError} When upload fails or returns unexpected status
   */
  async uploadCandidateResume(
    jobId: Job['id'],
    request: UploadResumeRequest,
  ): Promise<UploadResumeResponse> {
    const url = `job/${jobId}/resume-upload`;

    const { error, statusCode, data } = await useFFRestApiWithOptions({ parseError: true })(url)
      .post(request)
      .json<UploadResumeResponse>();

    if (error.value) {
      // Job not live
      if (error.value.includes('live job')) {
        throw new InternalError('Job must be active to upload resumes', {
          data: { jobId, error: error.value },
        });
      }

      // Contact mode requires copilot
      if (error.value.includes('active outreach')) {
        throw new InternalError('Contact mode requires AI copilot to be enabled', {
          data: { jobId, error: error.value },
        });
      }

      // Generic error
      throw new InternalError('Failed to upload resume', {
        data: { jobId, error: error.value },
      });
    }

    if (statusCode.value !== 200) {
      throw new InternalError('Resume upload returned unexpected status', {
        data: { jobId, statusCode: statusCode.value },
      });
    }

    return data.value as UploadResumeResponse;
  }
}
